import {ChangeEvent, useRef, useState} from "react";
import {BuildingData} from "../../models/remoteConstants.model";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {useImageUpload} from "../../utils/useImageUpload";
import {RemoveBuildingConstructionImageCommand} from "../../commands/setBuildingConstructionImage.command";
import {offerOrphanImageCleanup} from "../../utils/orphanImageCleanup";

const SLOT = 40;

// The Construction section of the building editor: one slot per construction
// turn, showing the building's ordered construction frames (a THEME asset in the
// top-level constructionImagePacks dict, keyed by building id). Fill left-to-right with
// no gaps — only the first empty slot is an "add" target; deleting a slot shifts
// later images down (splice). Clicking a slot opens a file dialog; the standard
// upload pipeline (applyUpload) handles the overwrite→archive + shared-image
// confirm, identically to every other image slot. Excess images beyond
// constructionTurns are intentionally left in the data (hidden, not trimmed).
function ConstructionImages(props: {
    building: BuildingData;
    // Flyout mode (Images tab): click a filled slot to select it for preview
    // instead of opening the upload dialog; the selected slot is ringed.
    selectedKey?: string;
    onSelectSlot?: (key: string) => void;
    // Drag a filled slot into the Workshop (carries the image key). When unset,
    // slots aren't draggable. Mirrors AssetPackGrid.
    onDragImageStart?: (key: string) => void;
    onDragImageEnd?: () => void;
}) {
    const building = props.building;
    const turns = Math.max(0, building.constructionTurns ?? 0);

    const {getImageUrl, getLocalizationValue, choose} = useApp();
    const {state, executeCommand, goBack} = useIntegrator();

    // Construction frames are a theme asset in the top-level dict, not on the building.
    const images = state.constructionImagePacks?.[building.id] ?? [];
    const {applyUpload} = useImageUpload();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const pending = useRef<{index: number; currentKey?: string}>({index: 0});
    const [hovered, setHovered] = useState<number | undefined>(undefined);

    if (turns <= 0)
        return <p className={'muted-text'}>No construction phase (Construction Turns is 0).</p>;

    function openFileDialog(index: number, currentKey?: string) {
        pending.current = {index, currentKey};
        fileInputRef.current?.click();
    }

    async function onFileChosen(e: ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = '';   // allow re-selecting the same file later
        if (!file) return;
        const {index, currentKey} = pending.current;
        await applyUpload({kind: 'buildingConstruction', buildingId: building.id, index}, currentKey, file);
    }

    async function onDelete(index: number) {
        const removedKey = (state.constructionImagePacks?.[building.id] ?? [])[index];
        // splice shifts later frames down — no gaps.
        executeCommand(new RemoveBuildingConstructionImageCommand(building.id, index));
        if (removedKey)
            await offerOrphanImageCleanup(removedKey, {state, getLocalizationValue, choose, executeCommand, goBack});
    }

    // Only the first empty slot accepts an upload, so the list stays gap-free.
    const firstEmpty = images.length;

    return (
        <div className={'layout horizontal gap'} style={{gap: 6, flexWrap: 'wrap', padding: 3}}>
            {Array.from({length: turns}).map((_, i) => {
                const key = images[i];
                const filled = !!key;
                const isAddSlot = !filled && i === firstEmpty;
                const clickable = filled || isAddSlot;
                const selectMode = !!props.onSelectSlot;
                const isSelected = filled && props.selectedKey === key;
                return (
                    <div key={i}
                         onClick={() => {
                             if (!clickable) return;
                             // Flyout select-mode: click a filled slot to preview it
                             // (replace is done via the flyout's upload button).
                             if (key && props.onSelectSlot) props.onSelectSlot(key);
                             else openFileDialog(i, key);
                         }}
                         onMouseEnter={() => filled && setHovered(i)}
                         onMouseLeave={() => setHovered(undefined)}
                         title={filled ? (selectMode ? `Construction ${i + 1}` : `Construction ${i + 1} — click to replace`)
                             : isAddSlot ? `Add construction image ${i + 1}`
                             : 'Fill the previous slot first'}
                         style={{
                             cursor: clickable ? 'pointer' : 'default', position: 'relative',
                             width: SLOT, height: SLOT, borderRadius: 4,
                             backgroundColor: 'black',
                             border: `solid 1px ${isSelected ? 'var(--tint-color)' : '#515151'}`,
                             boxShadow: isSelected ? '0 0 0 1px var(--tint-color)' : undefined,
                             opacity: clickable ? 1 : 0.35,
                         }}>
                        {filled
                            ? <img alt={key} src={getImageUrl(key)}
                                   draggable={!!props.onDragImageStart}
                                   onDragStart={e => {
                                       // Carry the image KEY, not the <img> URL — the
                                       // Workshop resolves keys; a URL reads as an unknown image.
                                       e.dataTransfer.setData('text/plain', key);
                                       e.dataTransfer.effectAllowed = 'copy';
                                       props.onDragImageStart?.(key);
                                   }}
                                   onDragEnd={() => props.onDragImageEnd?.()}
                                   style={{width: '100%', height: '100%', borderRadius: 4}}/>
                            : <div className={'layout vertical center'}
                                   style={{width: '100%', height: '100%', color: 'rgba(255,255,255,0.4)'}}>
                                {isAddSlot && <span className={'fa-solid fa-plus'} style={{fontSize: 10}}/>}
                              </div>}
                        {filled && hovered === i && (
                            <button onClick={e => { e.stopPropagation(); onDelete(i); }}
                                    title={'Remove this construction image'}
                                    style={{
                                        position: 'absolute', top: -6, right: -6,
                                        width: 16, height: 16, padding: 0, borderRadius: '50%',
                                        border: 'solid 1px #515151', background: 'var(--background-color)',
                                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                <span className={'fa-solid fa-xmark'} style={{fontSize: 9}}/>
                            </button>
                        )}
                    </div>
                );
            })}
            <input ref={fileInputRef} type={'file'} accept={'image/*'}
                   style={{display: 'none'}} onChange={onFileChosen}/>
        </div>
    );
}

export default ConstructionImages;
